// Browser-only Web Push client. The server owns VAPID private material.
import { base64UrlToUint8Array } from "./pushNotificationCore.mjs";
const API = "/api/push";
const tabId = (() => {
  try {
    const existing = sessionStorage.getItem("arflow_push_tab_id");
    if (existing) return existing;
    const value = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}_${Math.random()}`).replace(/[^a-zA-Z0-9_-]/g, "");
    sessionStorage.setItem("arflow_push_tab_id", value);
    return value;
  } catch { return "tab_" + Math.random().toString(36).slice(2, 14); }
})();

async function request(path, options = {}) {
  const response = await fetch(`${API}${path}`, { credentials: "same-origin", ...options });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || "Não foi possível atualizar as notificações.");
  return body;
}

export function pushSupported() {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export function getPushEnvironment() {
  const userAgent = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(userAgent)
    || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isStandalone = window.matchMedia?.("(display-mode: standalone)")?.matches === true
    || navigator.standalone === true;
  const isAndroid = /Android/i.test(userAgent);
  const isChrome = /Chrome|CriOS/i.test(userAgent);
  return { isIOS, isAndroid, isChrome, isStandalone };
}

async function getServerSubscriptionStatus({ endpoint, storeId, consultantId }) {
  if (!endpoint || storeId == null || consultantId == null) return { saved:false };
  const params = new URLSearchParams({ endpoint, storeId:String(storeId), consultantId:String(consultantId) });
  return request(`/subscription-status?${params.toString()}`);
}

export async function getPushStatus({ storeId, consultantId } = {}) {
  const environment = getPushEnvironment();
  if (!pushSupported()) {
    return {
      supported:false, permission:"unsupported", subscription:null,
      serviceWorkerRegistered:false, serviceWorkerActive:false, serverSaved:false, environment,
    };
  }
  const registered = await navigator.serviceWorker.register("/push-sw.js");
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  const serverStatus = subscription
    ? await getServerSubscriptionStatus({ endpoint:subscription.endpoint, storeId, consultantId }).catch(() => ({ saved:false }))
    : { saved:false };
  return {
    supported:true,
    permission:Notification.permission,
    subscription,
    serviceWorkerRegistered:!!registered,
    serviceWorkerActive:!!registration.active,
    serverSaved:serverStatus.saved === true,
    environment,
  };
}

export async function enablePush({ storeId, consultantId }) {
  if (!pushSupported()) throw new Error("Este navegador não oferece notificações push.");
  if (Notification.permission === "denied") throw new Error("As notificações foram bloqueadas nas configurações do navegador.");
  await navigator.serviceWorker.register("/push-sw.js");
  await navigator.serviceWorker.ready;
  const permission = Notification.permission === "granted" ? "granted" : await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Permissão de notificações não concedida.");
  const { publicKey } = await request("/public-key");
  const readyRegistration = await navigator.serviceWorker.ready;
  const existing = await readyRegistration.pushManager.getSubscription();
  const subscription = existing || await readyRegistration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlToUint8Array(publicKey),
    });
  await request("/subscribe", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ subscription: subscription.toJSON(), storeId, consultantId }),
  });
  return getPushStatus({ storeId, consultantId });
}

export async function disablePush({ storeId, consultantId }) {
  if (!pushSupported()) return;
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (subscription) {
    await request("/subscribe", {
      method: "DELETE", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ endpoint: subscription.endpoint, storeId, consultantId }),
    });
    await subscription.unsubscribe();
  }
}

export function sendPushTelemetry(action, eventId) {
  if (!eventId) return Promise.resolve();
  return request(`/telemetry/${action}`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ eventId }),
  }).catch(() => {});
}
export function getPushEvents() { return request("/events"); }

export function setPushPresence({ phone, storeId, consultantId, active }) {
  return request("/presence", {
    method: active ? "POST" : "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ phone, storeId, consultantId, tabId }),
  }).catch(() => {});
}